import {
  Checkbox,
  ChipsInput,
  InputChip,
  InputDescription,
  SelectionInputLayout,
  classNames,
  notification,
  toast,
  usePrevious,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { type GetWebcastSecurityQuery, useUpdateIpRangePolicyMutation } from 'src/generated/graphql-manager';

import { validateIpRange } from '../validators';

import styles from './ip-range-protection-policy.module.css';

type SecurityPolicyUnion = GetWebcastSecurityQuery['webcast']['securityPolicies'][number];

type IpRange = {
  value: string;
  valid: boolean;
  validationMessage?: string;
};

type Props = {
  ipRangePolicy?: Extract<SecurityPolicyUnion, { __typename?: 'IpRangePolicy' }>;
  formDisabled?: boolean;
  refetch: () => void;
};

const DEFAULT_IP_RANGE_ENABLED = false;

const mapIps = (ips: string[]): IpRange[] => ips.map((value) => ({ value, valid: true }));

export function IpRangeProtectionPolicy({ ipRangePolicy, formDisabled, refetch }: Props) {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();

  const [enabled, setEnabled] = useState(ipRangePolicy?.enabled ?? DEFAULT_IP_RANGE_ENABLED);
  const [ipRanges, setIpRanges] = useState<IpRange[]>(mapIps(ipRangePolicy?.ips ?? []));
  const [databaseIps, setDatabaseIps] = useState<string[]>(ipRangePolicy?.ips ?? []);
  const previousEnabled = usePrevious(enabled);

  const chipsInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ipRangePolicy) {
      setEnabled(ipRangePolicy.enabled ?? DEFAULT_IP_RANGE_ENABLED);
      setIpRanges(mapIps(ipRangePolicy.ips ?? []));
      setDatabaseIps(ipRangePolicy.ips ?? []);
    }
  }, [ipRangePolicy]);

  useEffect(() => {
    if (!previousEnabled && enabled) chipsInputRef.current?.focus();
  }, [enabled, previousEnabled]);

  const [updateIpRangePolicyMutation] = useUpdateIpRangePolicyMutation();

  // The mutation replaces the whole IP list, so firing several in parallel risks them committing
  // out of order and dropping entries. Keep at most one save in flight and coalesce any newer
  // request into a single follow-up that always carries the latest state.
  const savingRef = useRef(false);
  const pendingSaveRef = useRef<{ enabled: boolean; ips: string[] } | null>(null);

  const markInvalidIps = (invalidIps: string[]) => {
    const invalidIpSet = new Set(invalidIps);

    setIpRanges((previous) =>
      previous.map((ipRange) =>
        invalidIpSet.has(ipRange.value)
          ? {
              ...ipRange,
              valid: false,
              validationMessage: t(
                'manager.webcastSetup.eventAccess.accessControlMethods.ipRange.errorMessages.noValidIp'
              ),
            }
          : ipRange
      )
    );
  };

  const saveIpRangePolicy = (nextEnabled: boolean, ips: string[]) => {
    if (savingRef.current) {
      pendingSaveRef.current = { enabled: nextEnabled, ips };
      return;
    }

    savingRef.current = true;

    const flushPendingSave = () => {
      savingRef.current = false;
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = null;
      if (pending) saveIpRangePolicy(pending.enabled, pending.ips);
    };

    updateIpRangePolicyMutation({
      variables: { input: { webcastId, enabled: nextEnabled, ips } },
      onCompleted: (data) => {
        const result = data?.updateIpRangePolicy;

        if (result?.__typename === 'UpdateIpRangePolicySuccess') {
          setDatabaseIps(result.policy.ips);
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
          refetch();
        } else if (result?.__typename === 'UpdateIpRangePolicyIpValidationFailure') {
          markInvalidIps(result.invalidIps);
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
          refetch();
        }

        flushPendingSave();
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        flushPendingSave();
      },
    });
  };

  const validateIpRanges = (ranges: IpRange[]) => {
    const validatedRanges: IpRange[] = [];
    const validatedValues = new Set<string>();

    for (const range of ranges) {
      const validationMessage = validateIpRange(range.value, validatedValues);
      const valid = !validationMessage;

      validatedRanges.push({ value: range.value, valid, validationMessage });

      if (valid) validatedValues.add(range.value);
    }

    return validatedRanges;
  };

  const handleIpRangesChange = (ranges: IpRange[]) => {
    const validatedRanges = validateIpRanges(ranges);

    setIpRanges(validatedRanges);

    if (validatedRanges.every((range) => range.valid)) {
      const allDeleted = validatedRanges.length === 0 && databaseIps.length > 0;

      if (allDeleted && enabled) setEnabled(false);

      if (allDeleted || validatedRanges.length > 0) {
        saveIpRangePolicy(
          allDeleted ? false : enabled,
          validatedRanges.map((range) => range.value)
        );
      }
    }
  };

  const onChipsAdded = (values: string[]) => {
    handleIpRangesChange([...ipRanges, ...values.map((value) => ({ value, valid: true }))]);
  };

  const onChipDeleted = (index: number) => {
    handleIpRangesChange([...ipRanges.slice(0, index), ...ipRanges.slice(index + 1)]);
  };

  const onChipEdit = (value: string, index: number) => {
    handleIpRangesChange([...ipRanges.slice(0, index), { value, valid: true }, ...ipRanges.slice(index + 1)]);
  };

  const handleToggleIpRangeProtection = () => {
    const nextEnabled = !enabled;

    setEnabled(nextEnabled);

    // Only persist the toggle once there is at least one IP range to protect; enabling with an
    // empty list would lock everyone out, so we wait for the first entry (as restricted domains does).
    if (ipRanges.length > 0) {
      saveIpRangePolicy(
        nextEnabled,
        ipRanges.map((range) => range.value)
      );
    }
  };

  const validationMessage = useMemo(() => {
    const invalidRanges = ipRanges.filter((range) => !range.valid);

    if (invalidRanges.length === 0) return undefined;
    if (invalidRanges.length === 1) return invalidRanges[0].validationMessage;

    return t('manager.webcastSetup.eventAccess.accessControlMethods.ipRange.errorMessages.multipleErrors');
  }, [ipRanges, t]);

  const inputInfo = validationMessage ?? t('manager.webcastSetup.eventAccess.accessControlMethods.ipRange.inputInfo');

  return (
    <SelectionInputLayout
      variant="ghost"
      data-testid="ip-range-protection-policy"
      checked={enabled}
      disabled={formDisabled}
      inputElement={
        <Checkbox
          id="ip-range-protection-policy"
          data-testid="ip-range-protection-policy-checkbox"
          checked={enabled}
          disabled={formDisabled}
          onChange={handleToggleIpRangeProtection}
        >
          {t('manager.webcastSetup.eventAccess.accessControlMethods.ipRange.label')}
        </Checkbox>
      }
    >
      <InputDescription
        inputId="allowed-ip-ranges"
        hint={t('manager.webcastSetup.eventAccess.accessControlMethods.ipRange.hint')}
        label={`${t('manager.webcastSetup.eventAccess.accessControlMethods.ipRange.allowedIpRangesLabel')}${
          enabled ? ' *' : ''
        }`}
      >
        <ChipsInput
          ref={chipsInputRef}
          separators={['Enter', ',', ' ']}
          placeholder={t('manager.webcastSetup.eventAccess.accessControlMethods.ipRange.placeholder')}
          onChipsAdded={onChipsAdded}
          disabled={!enabled || formDisabled}
        >
          {ipRanges.map((ipRange, index) => (
            <InputChip
              key={`${ipRange.value}-${index}`}
              value={ipRange.value}
              invalid={!ipRange.valid}
              disabled={!enabled || formDisabled}
              onDelete={() => onChipDeleted(index)}
              onEdit={(value: string) => onChipEdit(value, index)}
            />
          ))}
        </ChipsInput>
      </InputDescription>

      <div
        data-testid="ip-range-input-info"
        className={classNames(styles.inputInfo, validationMessage && enabled && styles.invalid)}
      >
        {inputInfo}
      </div>
    </SelectionInputLayout>
  );
}
